// Input validation and sanitization utilities

export const validateEmail = (email: string): boolean => {
  const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
  return emailRegex.test(email);
};

export const validatePassword = (password: string): { isValid: boolean; errors: string[] } => {
  const errors: string[] = [];
  
  if (password.length < 8) {
    errors.push("Le mot de passe doit contenir au moins 8 caractères");
  }
  
  if (!/[A-Z]/.test(password)) {
    errors.push("Le mot de passe doit contenir au moins une majuscule");
  }
  
  if (!/[a-z]/.test(password)) {
    errors.push("Le mot de passe doit contenir au moins une minuscule");
  }
  
  if (!/[0-9]/.test(password)) {
    errors.push("Le mot de passe doit contenir au moins un chiffre");
  }
  
  return {
    isValid: errors.length === 0,
    errors
  };
};

export const validateUsername = (username: string): { isValid: boolean; errors: string[] } => {
  const errors: string[] = [];
  
  if (username.length < 3) {
    errors.push("Le nom d'utilisateur doit contenir au moins 3 caractères");
  }
  
  if (username.length > 30) {
    errors.push("Le nom d'utilisateur ne peut pas dépasser 30 caractères");
  }
  
  if (!/^[a-zA-Z0-9_]+$/.test(username)) {
    errors.push("Le nom d'utilisateur ne peut contenir que des lettres, chiffres et underscores");
  }
  
  return {
    isValid: errors.length === 0,
    errors
  };
};

export const validateName = (name: string, fieldName: string): { isValid: boolean; errors: string[] } => {
  const errors: string[] = [];
  
  if (name.length < 2) {
    errors.push(`${fieldName} doit contenir au moins 2 caractères`);
  }
  
  if (name.length > 50) {
    errors.push(`${fieldName} ne peut pas dépasser 50 caractères`);
  }
  
  if (!/^[a-zA-ZÀ-ÿ\s'-]+$/.test(name)) {
    errors.push(`${fieldName} ne peut contenir que des lettres, espaces, apostrophes et tirets`);
  }
  
  return {
    isValid: errors.length === 0,
    errors
  };
};

export const sanitizeInput = (input: string): string => {
  // Remove any HTML tags and escape special characters
  return input
    .replace(/<[^>]*>/g, '')
    .replace(/[<>&"']/g, (match) => {
      const escape: { [key: string]: string } = {
        '<': '&lt;',
        '>': '&gt;',
        '&': '&amp;',
        '"': '&quot;',
        "'": '&#x27;'
      };
      return escape[match];
    })
    .trim();
};

export const validateFormData = (
  email: string,
  password: string,
  username?: string,
  firstName?: string,
  lastName?: string,
  isSignUp: boolean = false
): { isValid: boolean; errors: string[] } => {
  const errors: string[] = [];
  
  // Validate email
  if (!validateEmail(email)) {
    errors.push("Format d'email invalide");
  }
  
  // Validate password
  const passwordValidation = validatePassword(password);
  if (!passwordValidation.isValid) {
    errors.push(...passwordValidation.errors);
  }
  
  // Validate signup fields
  if (isSignUp) {
    if (username) {
      const usernameValidation = validateUsername(username);
      if (!usernameValidation.isValid) {
        errors.push(...usernameValidation.errors);
      }
    }
    
    if (firstName) {
      const firstNameValidation = validateName(firstName, "Le prénom");
      if (!firstNameValidation.isValid) {
        errors.push(...firstNameValidation.errors);
      }
    }
    
    if (lastName) {
      const lastNameValidation = validateName(lastName, "Le nom");
      if (!lastNameValidation.isValid) {
        errors.push(...lastNameValidation.errors);
      }
    }
  }
  
  return {
    isValid: errors.length === 0,
    errors
  };
};